import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getScenarios } from '../services/api';
import api from '../services/api';
import { PhoneCall, ChevronRight, Lock, BookOpen, Award, CheckCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import OnboardingFlow from '../components/OnboardingFlow';
import useAuthStore from '../stores/authStore';

const ScenarioSelection = () => {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [scenarios, setScenarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [trainingStatus, setTrainingStatus] = useState(null);
  const [trainingRequired, setTrainingRequired] = useState(false);

  useEffect(() => {
    checkTrainingStatus();
    loadScenarios();

    // Check if user has completed onboarding
    const onboardingCompleted = localStorage.getItem('onboarding_completed');
    if (!onboardingCompleted) {
      // Show onboarding after scenarios load
      setTimeout(() => setShowOnboarding(true), 500);
    }
  }, []);

  const checkTrainingStatus = async () => {
    try {
      // Check if user has bypass_training enabled
      const userResponse = await api.get('/api/auth/me');
      const userData = userResponse.data.data.user;

      if (userData.bypassTraining === true) {
        setTrainingRequired(false);
        return;
      }

      // Only check for managers
      if (user?.role !== 'manager') {
        setTrainingRequired(false);
        return;
      }

      const response = await api.get('/api/training/status');
      const status = response.data.status;

      setTrainingStatus(status);
      setTrainingRequired(!status?.scenarios_unlocked);
    } catch (error) {
      console.error('Error checking training status:', error);
      // On error, assume training is required for managers
      if (user?.role === 'manager') {
        setTrainingRequired(true);
      }
    }
  };

  const loadScenarios = async () => {
    try {
      const data = await getScenarios();
      setScenarios(data);
    } catch (error) {
      console.error('Error loading scenarios:', error);
      toast.error('Ошибка загрузки сценариев');
    } finally {
      setLoading(false);
    }
  };

  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case 'medium':
        return 'bg-yellow-50 text-yellow-600 border-yellow-100';
      case 'hard':
        return 'bg-orange-50 text-orange-600 border-orange-100';
      case 'expert':
        return 'bg-red-50 text-red-600 border-red-100';
      default:
        return 'bg-green-50 text-green-600 border-green-100';
    }
  };

  const getDifficultyLabel = (difficulty) => {
    switch (difficulty) {
      case 'medium':
        return 'Средний';
      case 'hard':
        return 'Сложный';
      case 'expert':
        return 'Эксперт';
      default:
        return 'Легкий';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-600">Загрузка сценариев...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <OnboardingFlow
        isOpen={showOnboarding}
        onClose={() => setShowOnboarding(false)}
        onComplete={() => {
          toast.success('Добро пожаловать! Выберите первый сценарий');
        }}
      />

      <div className="min-h-screen bg-slate-50 p-8">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold text-slate-900 mb-2">
                  {trainingRequired ? 'Доступ к тренировкам' : 'Выберите сценарий тренировки'}
                </h1>
                <p className="text-slate-600">
                  {trainingRequired
                    ? 'Для доступа к практическим тренировкам необходимо пройти теоретическое обучение'
                    : 'Начните тренировку с AI-собеседником для развития навыков продаж'
                  }
                </p>
              </div>
              {!trainingRequired && (
                <button
                  onClick={() => setShowOnboarding(true)}
                  className="px-4 py-2 text-sm text-blue-600 hover:bg-blue-50 rounded-lg transition-colors border border-blue-200"
                >
                  Пройти обучение заново
                </button>
              )}
            </div>
          </div>

          {/* Training Required Block */}
          {trainingRequired ? (
            <div className="max-w-3xl mx-auto">
              <div className="bg-white rounded-2xl shadow-xl border border-slate-200 overflow-hidden">
                {/* Header with gradient */}
                <div className="bg-gradient-to-r from-blue-500 to-purple-600 p-8 text-white">
                  <div className="flex items-center justify-center mb-4">
                    <div className="bg-white bg-opacity-20 p-4 rounded-full">
                      <Lock className="w-12 h-12" />
                    </div>
                  </div>
                  <h2 className="text-3xl font-bold text-center mb-2">Обучение обязательно</h2>
                  <p className="text-center text-blue-50">
                    Сначала пройдите теоретическое обучение, чтобы получить доступ к практическим тренировкам
                  </p>
                </div>

                {/* Progress */}
                <div className="p-8">
                  {trainingStatus && (
                    <div className="mb-6">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-slate-700">Ваш прогресс</span>
                        <span className="text-sm font-bold text-blue-600">
                          {trainingStatus.modules_completed || 0} / {trainingStatus.total_modules || 14} модулей
                        </span>
                      </div>
                      <div className="w-full bg-slate-200 rounded-full h-3">
                        <div
                          className="bg-gradient-to-r from-blue-500 to-purple-600 h-3 rounded-full transition-all duration-500"
                          style={{
                            width: `${Math.round(((trainingStatus.modules_completed || 0) / (trainingStatus.total_modules || 14)) * 100)}%`
                          }}
                        />
                      </div>
                    </div>
                  )}

                  {/* Benefits */}
                  <div className="space-y-4 mb-8">
                    <h3 className="font-semibold text-slate-800 mb-3">Что вас ждет в обучении:</h3>

                    <div className="flex items-start space-x-3">
                      <div className="flex-shrink-0 w-6 h-6 bg-green-100 rounded-full flex items-center justify-center mt-0.5">
                        <CheckCircle className="w-4 h-4 text-green-600" />
                      </div>
                      <div>
                        <p className="font-medium text-slate-800">14 модулей по B2B продажам</p>
                        <p className="text-sm text-slate-600">Структурированная программа от основ до мастерства</p>
                      </div>
                    </div>

                    <div className="flex items-start space-x-3">
                      <div className="flex-shrink-0 w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center mt-0.5">
                        <BookOpen className="w-4 h-4 text-blue-600" />
                      </div>
                      <div>
                        <p className="font-medium text-slate-800">Практические материалы</p>
                        <p className="text-sm text-slate-600">Скрипты, техники, примеры из реальных кейсов</p>
                      </div>
                    </div>

                    <div className="flex items-start space-x-3">
                      <div className="flex-shrink-0 w-6 h-6 bg-purple-100 rounded-full flex items-center justify-center mt-0.5">
                        <Award className="w-4 h-4 text-purple-600" />
                      </div>
                      <div>
                        <p className="font-medium text-slate-800">Проверка знаний</p>
                        <p className="text-sm text-slate-600">Тесты после каждого модуля для закрепления материала</p>
                      </div>
                    </div>

                    <div className="flex items-start space-x-3">
                      <div className="flex-shrink-0 w-6 h-6 bg-orange-100 rounded-full flex items-center justify-center mt-0.5">
                        <PhoneCall className="w-4 h-4 text-orange-600" />
                      </div>
                      <div>
                        <p className="font-medium text-slate-800">Доступ к AI-тренировкам</p>
                        <p className="text-sm text-slate-600">После завершения обучения откроются все сценарии</p>
                      </div>
                    </div>
                  </div>

                  {/* CTA Button */}
                  <button
                    onClick={() => navigate('/training-mode')}
                    className="w-full bg-gradient-to-r from-blue-500 to-purple-600 text-white px-6 py-4 rounded-xl font-semibold hover:from-blue-600 hover:to-purple-700 transition-all shadow-lg hover:shadow-xl"
                  >
                    {trainingStatus?.modules_completed > 0 ? 'Продолжить обучение' : 'Начать обучение'}
                  </button>
                </div>
              </div>
            </div>
          ) : (
            /* Scenarios Grid */
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {scenarios.map((scenario) => (
                <div
                  key={scenario.id}
                  onClick={() => navigate(`/training/${scenario.id}`)}
                  className="bg-white rounded-xl border border-slate-200 p-6 hover:shadow-lg hover:border-blue-300 transition-all cursor-pointer group"
                >
                  {/* Icon */}
                  <div className="w-12 h-12 bg-blue-50 rounded-lg flex items-center justify-center mb-4 group-hover:bg-blue-100 transition-colors">
                    <PhoneCall className="w-6 h-6 text-blue-600" />
                  </div>

                  {/* Title */}
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">
                    {scenario.name}
                  </h3>

                  {/* Description */}
                  <p className="text-sm text-slate-600 mb-4">{scenario.description}</p>

                  {/* Meta Info */}
                  <div className="flex items-center gap-2 mb-4">
                    <span
                      className={`px-2 py-1 rounded-md text-xs font-medium border ${getDifficultyColor(
                        scenario.difficulty
                      )}`}
                    >
                      {getDifficultyLabel(scenario.difficulty)}
                    </span>
                    <span className="text-xs text-slate-500">
                      {scenario.character?.role === 'secretary' ? '👩‍💼 Секретарь' : '👨‍💼 Директор'}
                    </span>
                  </div>

                  {/* Objectives */}
                  <div className="mb-4">
                    <p className="text-xs font-semibold text-slate-400 uppercase mb-2">
                      Цели:
                    </p>
                    <ul className="space-y-1">
                      {scenario.objectives?.slice(0, 3).map((obj, i) => (
                        <li key={i} className="text-xs text-slate-600 flex items-start gap-2">
                          <span className="text-blue-500 mt-0.5">•</span>
                          <span>{obj}</span>
                        </li>
                      ))}
                      {scenario.objectives?.length > 3 && (
                        <li className="text-xs text-slate-400">
                          +{scenario.objectives.length - 3} еще...
                        </li>
                      )}
                    </ul>
                  </div>

                  {/* Start Button */}
                  <button className="w-full py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center gap-2 group-hover:shadow-md">
                    Начать тренировку
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default ScenarioSelection;
